// Bulk Actions imports — restore when implementing Bulk Actions feature
// import { makeStyles, Theme } from '@material-ui/core';
import { Settings } from 'luxon';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import {
  FanControllerStateFragmentFragment,
  FanHeartbeatErrorCode,
  GrainBinDtoFragmentFragment,
  GrainBinFanControllerFragmentFragment,
  withGetGrainBinSitesHoc,
  WithGetGrainBinSitesHocChildProps,
  withGetOpsViewColsVisibilityPrefsHoc,
  WithGetOpsViewColsVisibilityPrefsHocChildProps,
  withGetOpsViewRowsVisibilityPrefsHoc,
  WithGetOpsViewRowsVisibilityPrefsHocChildProps,
} from '../../../../core/src/api';
import { AggregatedSitesContext } from '../../../../core/src/contexts';
// Bulk Actions imports — restore when implementing Bulk Actions feature
// import { makeStyles, Theme } from '@material-ui/core';
// import { black_shade_9 } from '../../../../core/src/style';
// import { PrefJsonItem } from './manage-sites/ManageSitesTable';
// import { BulkActionsView } from './BulkActionsView';
import { formatGrainType } from '../../../../core/src/util';
import { isQualifyingFanHeartbeatErrorCode } from '../grain-container/aeration/FanControllerErrorDisplay';
import { getSimpleFanStatus, SimpleFanStatus } from '../grain-container/aeration/FanStatusHelpers';
import { DialogSpinner } from '../spinner';
// Bulk Actions imports — restore when implementing Bulk Actions feature
// import { BulkActionsView } from './BulkActionsView';
// import { PrefJsonItem } from './manage-sites/ManageSitesTable';
import { UNASSIGNED_BINS_SITE_ID } from './manage-sites';
import { OperationsViewTable } from './OperationsViewTable';
import { getValidOperationsViewRowsVisibilityPrefs } from './OperationsViewTableHelpers';

// Bulk Actions styles — restore when implementing Bulk Actions feature
// const useStyles = makeStyles((theme: Theme) => ({
//   infoText: {
//     fontSize: 20,
//     position: 'absolute',
//     transform: 'translate(0, -50%)',
//     top: '50%',
//     left: '40%',
//     zIndex: 2,
//     color: black_shade_9,
//     fontWeight: 600,
//   },
// }));

type fanControllerDTO = {
  id: number;
  state: boolean;
  active: string;
  fan_attributes: GrainBinFanControllerFragmentFragment;
  fan_config_state: FanControllerStateFragmentFragment;
};

export type GrainBinAggregationDataType = {
  grain_bin_id: number;
  grain_bin_attributes: GrainBinDtoFragmentFragment;
  fan_controllers: fanControllerDTO[];
};

export type OpsViewVisibilityPrefsType = {
  site_id: number;
  site_name: string;
  bins: { bin_id: number; bin_name: string; hide: boolean }[];
};

const GrainOperationsBase: FunctionComponent<
  {
    grain_bin_ids: number[];
    goContainer: (args: any) => void;
  } & WithGetGrainBinSitesHocChildProps &
    WithGetOpsViewColsVisibilityPrefsHocChildProps &
    WithGetOpsViewRowsVisibilityPrefsHocChildProps &
    RouteComponentProps
> = ({
  grain_bin_ids,
  grain_bin_sites,
  grain_sites_loading,
  goContainer,
  loading,
  operations_view_cols_visibility_prefs,
  rows_visibility_prefs_loading,
  operations_view_rows_visibility_prefs,
}) => {
  // Bulk Actions imports — restore when implementing Bulk Actions feature
  // const classes = useStyles();

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  Settings.defaultLocale = 'en-US';
  Settings.defaultZone = browserTimezone;

  const unassignedSiteName = 'UNASSIGNED_BINS';

  // Aggregated view is active when the backend returns sites with a non-null account_name.
  // This happens when a special viewer account (e.g. CHS, USGC Mexico) queries their own
  // account_id — the resolver fans out to all child accounts and tags each site with the
  // child account's name. Normal single-account users always get account_name = null.
  const is_aggregated_view = grain_bin_sites.some((site) => site.account_name != null);

  // In aggregated view we always want the tree hierarchy visible, so force groupBySites=true.
  // For normal accounts, default to true only if the account has at least one named site.
  const hasAnySiteCreated = grain_bin_sites.some(
    (site) => site.site_id !== UNASSIGNED_BINS_SITE_ID
  );
  const [groupBySites, setGroupBySites] = useState(is_aggregated_view || hasAnySiteCreated);

  // Build site-level group rows for the AG Grid tree.
  // Normal view:      [site_name]               → 1-level group
  // Aggregated view:  [account_name, site_name] → 2-level group (account > site)
  // Named sites only — UNASSIGNED_BINS is handled separately below.
  let grainSitesHierarchyData = grain_bin_sites
    .filter((site) => site.site_name !== unassignedSiteName)
    .map((site) => {
      const siteHierarchy =
        is_aggregated_view && site.account_name
          ? [site.account_name, site.site_name]
          : [site.site_name];
      return {
        siteHierarchy,
        site_id: site.site_id,
      };
    });

  // In aggregated view, prepend one account-level group row per unique account_name.
  // These become the top-level nodes in the tree: Account > Site > Bin.
  if (is_aggregated_view) {
    const seen_accounts = new Set<string>();
    const account_rows: any[] = [];
    grain_bin_sites.forEach((site) => {
      if (site.account_name && !seen_accounts.has(site.account_name)) {
        seen_accounts.add(site.account_name);
        account_rows.push({
          site_id: `account_${site.account_name}`,
          siteHierarchy: [site.account_name],
        });
      }
    });
    grainSitesHierarchyData = [...account_rows, ...grainSitesHierarchyData];
  }

  const grainBins: GrainBinAggregationDataType[] = grain_bin_sites.reduce((acc: any, site) => {
    if (site && site.bins.length > 0) acc.push(...site.bins);
    return acc;
  }, []);

  const unassignedBins = grainBins.filter((bin) => {
    const site_id = bin.grain_bin_attributes.site
      ? bin.grain_bin_attributes.site.site_id
      : UNASSIGNED_BINS_SITE_ID;
    return site_id === UNASSIGNED_BINS_SITE_ID;
  });
  const unassignedBinsIds = unassignedBins.map(({ grain_bin_id }) => grain_bin_id);

  // Maps each grain_bin_id to its account_name so bin rows can build the correct
  // siteHierarchy path when rendering in aggregated view.
  const bin_account_name_map = new Map<number, string | null>();
  if (is_aggregated_view) {
    grain_bin_sites.forEach((site) => {
      site.bins.forEach((bin) => {
        if (bin) {
          bin_account_name_map.set(bin.grain_bin_id, site.account_name as string);
        }
      });
    });
  }

  // In normal view, bins with no site assignment are grouped under an UNASSIGNED_BINS row.
  // In aggregated view, those bins sit directly under their account node instead — the
  // intermediate UNASSIGNED_BINS row would add a confusing extra level to the tree.
  if (!is_aggregated_view) {
    grainSitesHierarchyData = [
      ...grainSitesHierarchyData,
      ...(unassignedBins.length > 0
        ? [{ site_id: UNASSIGNED_BINS_SITE_ID, siteHierarchy: [unassignedSiteName] }]
        : []),
    ];
  }

  let totalFansCount = 0;
  const grainBinsHierarchyData = grainBins.map((bin) => {
    const grain_bin_attributes = bin.grain_bin_attributes;
    const fan_controllers = bin.fan_controllers;
    const has_errored_fan = fan_controllers.some(
      (fc) =>
        fc.fan_config_state && isQualifyingFanHeartbeatErrorCode(fc.fan_config_state.error_code)
    );
    const has_fan_start_fail_error =
      has_errored_fan &&
      fan_controllers.some(
        (fc) =>
          fc.fan_config_state && fc.fan_config_state.error_code === FanHeartbeatErrorCode.StartFail
      );
    const has_fan_stop_fail_error =
      has_errored_fan &&
      fan_controllers.some(
        (fc) =>
          fc.fan_config_state && fc.fan_config_state.error_code === FanHeartbeatErrorCode.StopFail
      );
    totalFansCount += fan_controllers.length;
    const bin_fan_status = fan_controllers.map((fc) => ({
      grain_bin_fan_controller_id: fc.fan_attributes.grain_bin_fan_controller_id,
      fan_name: fc.fan_attributes.alias,
      state: fc.fan_config_state.value_next,
      is_on: fc.fan_config_state.is_on,
      has_override: fc.fan_config_state.is_override,
      error_code: fc.fan_config_state.error_code,
    }));

    const any_fan_running = fan_controllers.some(
      (ctrl) => ctrl.fan_config_state && ctrl.fan_config_state.is_on
    );
    const fan_statuses = fan_controllers.map((ctrl, ix) => {
      return {
        id: ctrl.fan_attributes.fan_controller_id_next,
        fan_name: ctrl.fan_attributes.alias || `Fan ${ix}`,
        ...getSimpleFanStatus(ctrl.fan_attributes),
      };
    });
    const has_any_fan_ready = fan_statuses.some(({ status }) => status === SimpleFanStatus.READY);

    return {
      has_any_fan_ready,
      any_fan_running,
      has_errored_fan,
      has_fan_start_fail_error,
      has_fan_stop_fail_error,
      bin_fan_status,
      grain_bin_id: bin.grain_bin_id,
      bin_name: bin.grain_bin_attributes.bin_name,
      siteHierarchy: (() => {
        const site_name = bin.grain_bin_attributes.site
          ? bin.grain_bin_attributes.site.site_name
          : 'UNASSIGNED_BINS';
        const bin_name = bin.grain_bin_attributes.bin_name;
        if (is_aggregated_view) {
          const acct_name = bin_account_name_map.get(bin.grain_bin_id);
          const resolved_account = acct_name || 'Unknown Account';
          // Bins with a named site: Account > Site > Bin (3 levels)
          // Bins with no site:      Account > Bin   (2 levels, no intermediate row)
          if (site_name === unassignedSiteName) {
            return [resolved_account, bin_name];
          }
          return [resolved_account, site_name, bin_name];
        }
        // Normal single-account view: Site > Bin (2 levels)
        return [site_name, bin_name];
      })(),
      automation_info: {
        enabled: grain_bin_attributes.enable_fan_guidance,
        fan_guidance_start_date: grain_bin_attributes.fan_guidance_start_date,
        fan_guidance_end_date: grain_bin_attributes.fan_guidance_end_date,
      },
      grain_type: formatGrainType(grain_bin_attributes.grain_type),
      grain_bin_location_timezone: grain_bin_attributes.tz || browserTimezone,
      grain_bin_recommendation_type: grain_bin_attributes.recommendation_type,
      last_week_co2_alert_sent_on: null,
      // loading columns — updated via gridApi.forEachNode once async queries complete
      mode: 'loading',
      grain_level: 'loading',
      fan_schedule: 'loading',
      last_week_airspace_history: 'loading',
      last_aeration_run: 'loading',
      weather: 'loading',
    };
  });

  const sitesHierarchyData = [...grainSitesHierarchyData, ...grainBinsHierarchyData];
  const operationsViewInfo = {
    // In normal view, subtract 1 for the synthetic UNASSIGNED_BINS entry.
    // In aggregated view, each child account may have its own UNASSIGNED_BINS entry — exclude all of them.
    sitesCount: is_aggregated_view
      ? grain_bin_sites.filter((site) => site.site_id !== UNASSIGNED_BINS_SITE_ID).length
      : Math.max(0, grain_bin_sites.length - 1),
    binsCount: grainBins.length,
    fansCount: totalFansCount,
    allSitesGrainLevelCount: 0,
  };
  const sortedGrainBinsHierarchyData = grainBinsHierarchyData.sort((a, b) =>
    a.bin_name.toLowerCase() > b.bin_name.toLowerCase() ? 1 : -1
  );

  // Special viewer accounts have no bins linked directly to their account_id, so
  // grain_bin_ids from the HOC (which comes from account.grain_bin_links) is empty.
  // Derive the effective IDs from the aggregated sites data so the async column HOCs
  // (aeration schedule, grain level, airspace, weather) receive the correct bin IDs.
  const effective_grain_bin_ids = is_aggregated_view
    ? grainBins.map((b) => b.grain_bin_id)
    : grain_bin_ids;
  const initial_grain_bin_ids = effective_grain_bin_ids.slice(0, 5); // first batch for weather

  const allActiveSitesIds: number[] = grain_bin_sites.map((site) => site.site_id);

  // Build a map of site_id → bin_ids from live data so getValidOperationsViewRowsVisibilityPrefs
  // can inject bins added to named sites after the pref was last saved.
  const liveSitesBins: Record<number, number[]> = {};
  grain_bin_sites.forEach((site) => {
    if (site.site_id !== UNASSIGNED_BINS_SITE_ID) {
      liveSitesBins[site.site_id] = site.bins.filter(Boolean).map((b) => b!.grain_bin_id);
    }
  });

  const valid_operations_view_rows_visibility_prefs = getValidOperationsViewRowsVisibilityPrefs({
    allActiveSitesIds,
    operations_view_rows_visibility_prefs,
    unassignedBinsIds,
    liveSitesBins,
  });

  useEffect(() => {
    setGroupBySites(is_aggregated_view || hasAnySiteCreated);
  }, [hasAnySiteCreated, is_aggregated_view]);

  if (grain_sites_loading || loading || rows_visibility_prefs_loading) {
    return <DialogSpinner open title="Loading Sites..." />;
  }

  return (
    <AggregatedSitesContext.Provider value={{ loading: false, sites: grain_bin_sites as any[] }}>
      <div style={{ marginTop: 15 }}>
        {/* Bulk Actions — hidden until feature is ready
      <div style={{ position: 'relative' }}>
        <div className={classes.infoText}>Coming Soon</div>
        <BulkActionsView />
      </div>
      */}
        <div style={{ marginTop: 15 }}>
          <OperationsViewTable
            sitesHierarchyData={sitesHierarchyData}
            sortedGrainBinsHierarchyData={sortedGrainBinsHierarchyData}
            initial_grain_bin_ids={initial_grain_bin_ids}
            grain_bin_ids={effective_grain_bin_ids}
            goContainer={goContainer}
            operationsViewInfo={operationsViewInfo}
            groupBySites={groupBySites}
            setGroupBySites={setGroupBySites}
            is_aggregated_view={is_aggregated_view}
            operations_view_cols_visibility_prefs={operations_view_cols_visibility_prefs}
            valid_operations_view_rows_visibility_prefs={
              valid_operations_view_rows_visibility_prefs
            }
          />
        </div>
      </div>
    </AggregatedSitesContext.Provider>
  );
};

export const GrainOperations = withGetOpsViewRowsVisibilityPrefsHoc(
  withGetOpsViewColsVisibilityPrefsHoc(withGetGrainBinSitesHoc(GrainOperationsBase))
);
